import { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { Heart, Menu, X } from "lucide-react";
import { useShopCategories, useShopCategoriesWithImages, useShopMegaMenuFeatured } from "@/lib/api.ts";
import { toUrlFriendly } from "@/lib/utils.ts";
import { getWishlist } from "@/lib/wishlist.ts";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch("/api/pg/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "shop" }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return <p className="text-sm font-semibold text-emerald-600">Thanks for subscribing!</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        required
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="min-w-0 flex-1 border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-heading"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="whitespace-nowrap bg-heading px-5 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {status === "loading" ? "..." : "Subscribe"}
      </button>
    </form>
  );
}

export function ShopLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(() => getWishlist().length);
  const location = useLocation();
  const { data } = useShopCategories();
  const { data: catImagesData } = useShopCategoriesWithImages();
  const { data: megaMenuData } = useShopMegaMenuFeatured();
  const categories = data?.categories ?? [];
  const categoriesWithImages = catImagesData?.categories ?? [];
  const megaFeatured = megaMenuData?.featured ?? {};
  const megaRef = useRef<HTMLDivElement | null>(null);
  const [megaOpen, setMegaOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const megaTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => setWishlistCount(getWishlist().length);
    window.addEventListener("wishlist-change", handler);
    return () => window.removeEventListener("wishlist-change", handler);
  }, []);

  const openMega = () => {
    clearTimeout(megaTimeout.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    megaTimeout.current = setTimeout(() => {
      setMegaOpen(false);
      setHoveredCategory(null);
    }, 150);
  };

  // Split categories into 2 columns for mega menu left side
  const colSize = Math.ceil(categories.length / 2);
  const megaCols = [
    categories.slice(0, colSize),
    categories.slice(colSize),
  ].filter((c) => c.length > 0);

  // Featured categories: top 2 by count that have images (fallback)
  const featuredCategories = categoriesWithImages
    .filter((c) => c.imageUrl)
    .slice(0, 2);

  // Mega menu featured: use hovered category products, or default
  const activeFeatured =
    (hoveredCategory && megaFeatured[hoveredCategory]) ||
    megaFeatured["__default__"] ||
    null;

  return (
    <div className="flex min-h-screen flex-col">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-white">
        <div className="mx-auto flex h-16 max-w-[1360px] items-center justify-between px-6 lg:px-10">
          {/* Left: Burger (mobile) + Logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <Link to="/" className="no-underline hover:no-underline">
              <span className="text-[1.75rem] font-extrabold tracking-tight text-heading" style={{ letterSpacing: '-0.02em' }}>
                tst<span className="text-indigo-500">.</span>
              </span>
            </Link>
          </div>

          {/* Center: Desktop nav */}
          <ul className="hidden list-none items-center gap-8 lg:flex">
            <li className="group">
              <Link
                to="/"
                className="relative py-5 text-[13px] font-medium uppercase tracking-widest text-heading transition-colors hover:text-text-muted"
              >
                Home
                <span className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-heading transition-transform duration-200 group-hover:scale-x-100" />
              </Link>
            </li>
            <li
              className="group"
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
            >
              <button
                className="relative flex items-center gap-1 py-5 text-[13px] font-medium uppercase tracking-widest text-heading transition-colors hover:text-text-muted"
              >
                Categories
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  className={`ml-0.5 transition-transform ${megaOpen ? "rotate-180" : ""}`}
                >
                  <path d="M3 5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className={`absolute inset-x-0 bottom-0 h-[2px] bg-heading transition-transform duration-200 origin-left ${megaOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
              </button>
            </li>
            <li className="group">
              <Link
                to="/about-us"
                className="relative py-5 text-[13px] font-medium uppercase tracking-widest text-heading transition-colors hover:text-text-muted"
              >
                About
                <span className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-heading transition-transform duration-200 group-hover:scale-x-100" />
              </Link>
            </li>
            <li className="group">
              <a
                href="/blog/"
                className="relative py-5 text-[13px] font-medium uppercase tracking-widest text-heading transition-colors hover:text-text-muted"
              >
                Blog
                <span className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-heading transition-transform duration-200 group-hover:scale-x-100" />
              </a>
            </li>
          </ul>

          {/* Right: Wishlist */}
          <Link to="/wishlist" className="relative" aria-label="Wishlist">
            <Heart size={20} className="text-heading" />
            {wishlistCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
                {wishlistCount}
              </span>
            )}
          </Link>
        </div>
      </nav>

      {/* Mega Menu — Full-width Prestige-style */}
      <div
        ref={megaRef}
        onMouseEnter={openMega}
        onMouseLeave={closeMega}
        className={`fixed inset-x-0 top-[65px] z-40 hidden transition-all duration-300 lg:block ${
          megaOpen
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`fixed inset-0 top-[65px] bg-black/20 transition-opacity duration-300 ${
            megaOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMegaOpen(false)}
        />
        {/* Panel */}
        <div className="relative border-t border-border bg-white shadow-lg">
          <div className="mx-auto flex max-w-[1360px] gap-12 px-6 py-10 lg:px-10">
            {/* Left: Category links (2 columns) */}
            <div className="w-[40%] shrink-0">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-text-muted">
                Shop by Category
              </p>
              <div className="grid grid-cols-2 gap-x-10 gap-y-0.5">
                {megaCols.map((col, ci) => (
                  <div key={ci} className="space-y-0.5">
                    {col.map((cat) => (
                      <Link
                        key={cat.category}
                        to={`/collection/${toUrlFriendly(cat.category ?? "other")}`}
                        onMouseEnter={() => setHoveredCategory(cat.category ?? null)}
                        className={`group/link flex items-center justify-between py-2 text-sm transition-colors ${
                          hoveredCategory === cat.category
                            ? "text-heading font-medium"
                            : "text-heading hover:text-text-muted"
                        }`}
                      >
                        <span className="border-b border-transparent transition-colors group-hover/link:border-heading">
                          {cat.category}
                        </span>
                        <span className="text-xs text-text-muted">{cat.count}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Featured products (dynamic on hover) */}
            <div className="flex-1">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-text-muted">
                Featured
              </p>
              <div
                key={hoveredCategory ?? "__default__"}
                className="grid grid-cols-2 gap-6 mega-fade-in"
              >
                {(activeFeatured ?? featuredCategories.map((c) => ({
                  name: c.productName ?? c.category ?? "",
                  slug: c.productSlug ?? toUrlFriendly(c.category ?? "other"),
                  imageUrl: c.imageUrl,
                  category: c.category,
                }))).map((item, i) => (
                  <Link
                    key={`${hoveredCategory}-${i}`}
                    to={`/product/${item.slug}`}
                    className="group/card"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-bg-alt">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-text-muted text-sm">
                          No image
                        </div>
                      )}
                    </div>
                    <p className="mt-3 text-sm font-medium text-heading transition-colors group-hover/card:text-text-muted">
                      {item.name}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-[1000] flex flex-col bg-heading transition-opacity duration-300 lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="flex justify-end p-5">
          <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <X size={24} className="text-white" />
          </button>
        </div>
        <ul className="flex flex-1 flex-col items-center justify-center gap-6 list-none p-0">
          <li>
            <Link to="/" className="text-2xl font-light tracking-wide text-white">
              Home
            </Link>
          </li>
          <li className="mt-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">
            Collections
          </li>
          {categories.map((cat) => (
            <li key={cat.category}>
              <Link
                to={`/collection/${toUrlFriendly(cat.category ?? "other")}`}
                className="text-lg font-light text-white/80 transition-colors hover:text-white"
              >
                {cat.category}
              </Link>
            </li>
          ))}
          <li className="mt-4">
            <Link to="/about-us" className="text-2xl font-light tracking-wide text-white">
              About
            </Link>
          </li>
          <li>
            <a href="/blog/" className="text-2xl font-light tracking-wide text-white">
              Blog
            </a>
          </li>
          <li>
            <Link to="/wishlist" className="text-2xl font-light tracking-wide text-white">
              Wishlist
            </Link>
          </li>
        </ul>
      </div>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-bg-alt">
        <div className="mx-auto max-w-[1360px] px-6 py-16 lg:px-10">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_1.5fr]">
            {/* About */}
            <div>
              <h3 className="mb-4 text-[1.5rem] font-extrabold text-heading" style={{ letterSpacing: '-0.02em' }}>
                tst<span className="text-indigo-500">.</span>
              </h3>
              <p className="text-sm leading-relaxed text-text-muted">
                Curated products from top brands. We help you discover the best deals with expert reviews and honest recommendations.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-heading">
                Quick Links
              </h3>
              <ul className="list-none space-y-2 p-0">
                <li><Link to="/" className="text-sm text-text-muted transition-colors hover:text-heading">Home</Link></li>
                <li><Link to="/about-us" className="text-sm text-text-muted transition-colors hover:text-heading">About Us</Link></li>
                <li><Link to="/contact-us" className="text-sm text-text-muted transition-colors hover:text-heading">Contact</Link></li>
                <li><a href="/blog/" className="text-sm text-text-muted transition-colors hover:text-heading">Blog</a></li>
                <li><Link to="/wishlist" className="text-sm text-text-muted transition-colors hover:text-heading">Wishlist</Link></li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-heading">
                Categories
              </h3>
              <ul className="list-none space-y-2 p-0">
                {categories.slice(0, 6).map((cat) => (
                  <li key={cat.category}>
                    <Link
                      to={`/collection/${toUrlFriendly(cat.category ?? "other")}`}
                      className="text-sm text-text-muted transition-colors hover:text-heading"
                    >
                      {cat.category}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-heading">
                Legal
              </h3>
              <ul className="list-none space-y-2 p-0">
                <li><Link to="/privacy-policy" className="text-sm text-text-muted transition-colors hover:text-heading">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-sm text-text-muted transition-colors hover:text-heading">Terms of Service</Link></li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-heading">
                Never Miss a Deal
              </h3>
              <p className="text-sm leading-relaxed text-text-muted">
                Get the best reviews and exclusive deals delivered to your inbox weekly.
              </p>
              <div className="mt-4">
                <NewsletterForm />
              </div>
              <p className="mt-2 text-xs text-text-muted">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-between border-t border-border pt-8">
            <span className="text-xs text-text-muted">
              &copy; {new Date().getFullYear()} The Secret Trends. All rights reserved.
            </span>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/profile.php?id=61554739223968" target="_blank" rel="noopener" aria-label="Facebook" className="text-text-muted transition-colors hover:text-heading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/the.secret.trends/" target="_blank" rel="noopener" aria-label="Instagram" className="text-text-muted transition-colors hover:text-heading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.pinterest.com/thesecrettrends/" target="_blank" rel="noopener" aria-label="Pinterest" className="text-text-muted transition-colors hover:text-heading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@thesecrettrends" target="_blank" rel="noopener" aria-label="TikTok" className="text-text-muted transition-colors hover:text-heading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
